import { zodResolver } from '@hookform/resolvers/zod';
import { useId, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import {
  type Board,
  boardColor,
  type Column,
  useDeleteBoard,
  useUpdateBoard,
} from '@/entities/board';
import { FormError, useFieldError, useServerErrors } from '@/shared/forms';
import { Button, Combobox, ConfirmDialog, TextAreaField, TextField } from '@/shared/ui';

import { BoardColorPicker } from './BoardColorPicker';
import { WorkweekPicker } from './WorkweekPicker';
import { sameDays } from './workdays';
import styles from './BoardManage.module.css';

const schema = z.object({
  name: z.string().trim().min(1, 'REQUIRED').max(200, 'STRING_TOO_LONG'),
  description: z.string().max(5000, 'STRING_TOO_LONG'),
});
type Values = z.infer<typeof schema>;

interface GeneralProps {
  board: Board;
  /** Active columns, for choosing the default column of quick create. */
  columns: Column[];
  onSaved: () => void;
  onCancel: () => void;
}

/** "General": name, description, colour and the default creation column. */
export function BoardGeneralForm({ board, columns, onSaved, onCancel }: GeneralProps) {
  const { t, i18n } = useTranslation();
  const id = useId();
  const fieldError = useFieldError();
  const update = useUpdateBoard(board);
  const [color, setColor] = useState<string | null>(board.color);
  const [workweek, setWorkweek] = useState<number[]>(board.workweek_days);
  const defaultColumnValid = columns.some((c) => c.id === board.default_column_id);
  const [defaultColumn, setDefaultColumn] = useState<string>(
    defaultColumnValid ? (board.default_column_id ?? '') : '',
  );
  const { register, handleSubmit, formState, setError } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { name: board.name, description: board.description ?? '' },
  });
  const serverErrors = useServerErrors(setError, ['name', 'description']);

  const onSubmit = handleSubmit((values) => {
    serverErrors.clear();
    update.mutate(
      {
        name: values.name,
        description: values.description.trim() || null,
        ...(color !== board.color && { color }),
        ...(!sameDays(workweek, board.workweek_days) && { workweek_days: workweek }),
        ...(defaultColumn !== (defaultColumnValid ? (board.default_column_id ?? '') : '') && {
          default_column_id: defaultColumn || null,
        }),
      },
      { onSuccess: onSaved, onError: serverErrors.handle },
    );
  });

  return (
    <form className={styles.settingsForm} onSubmit={(e) => void onSubmit(e)} noValidate>
      <FormError error={serverErrors.formError} />
      <TextField
        label={t('boards.name')}
        error={fieldError(formState.errors.name?.message)}
        {...register('name')}
      />
      <TextAreaField
        label={t('boards.description')}
        rows={4}
        error={fieldError(formState.errors.description?.message)}
        {...register('description')}
      />
      <BoardColorPicker
        value={color}
        defaultColor={boardColor({ ...board, color: null })}
        onChange={setColor}
        {...(board.workspace_id && { workspaceId: board.workspace_id })}
      />
      <div className={styles.selectField}>
        <label htmlFor={`${id}-default-column`}>{t('boards.defaultColumn')}</label>
        {/* The system Calendar column is never the default destination. */}
        <Combobox
          id={`${id}-default-column`}
          label={t('boards.defaultColumn')}
          value={defaultColumn === '' ? '__first__' : defaultColumn}
          searchPlaceholder={t('common.search')}
          emptyText={t('common.noResults')}
          options={[
            { value: '__first__', label: t('boards.defaultColumnFirst') },
            ...columns
              .filter((column) => column.system_kind === null)
              .map((column) => ({ value: column.id, label: column.name })),
          ]}
          onChange={(next) => {
            setDefaultColumn(next === '__first__' ? '' : next);
          }}
        />
        <small>{t('boards.defaultColumnHint')}</small>
      </div>
      <WorkweekPicker value={workweek} onChange={setWorkweek} locale={i18n.language} />
      <div className={styles.settingsActions}>
        <span className={styles.spacer} />
        <Button onClick={onCancel}>{t('common.cancel')}</Button>
        <Button type="submit" variant="primary" disabled={update.isPending}>
          {t('common.save')}
        </Button>
      </div>
    </form>
  );
}

/** Trash the board (restorable from the workspace trash). */
export function BoardDeleteSection({ board, onDeleted }: { board: Board; onDeleted: () => void }) {
  const { t } = useTranslation();
  const remove = useDeleteBoard(board);
  const [confirming, setConfirming] = useState(false);
  return (
    <section className={styles.settingsForm} aria-label={t('boards.delete')}>
      <p>{t('boards.deleteSectionHint')}</p>
      <div className={styles.settingsActions}>
        <Button
          variant="danger"
          onClick={() => {
            setConfirming(true);
          }}
        >
          {t('boards.delete')}
        </Button>
      </div>
      {confirming && (
        <ConfirmDialog
          title={t('boards.deleteTitle')}
          message={t('boards.deleteMessage', { name: board.name })}
          confirmLabel={t('common.delete')}
          pending={remove.isPending}
          onCancel={() => {
            setConfirming(false);
          }}
          onConfirm={() => {
            remove.mutate(undefined, { onSuccess: onDeleted });
          }}
        />
      )}
    </section>
  );
}
